import * as React from 'react'

import { ChevronDownIcon } from 'lucide-react'
import { action, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'
import { createPortal } from 'react-dom'

import Localization from '~/client/src/shared/localization/LocalizationManager'

import './NavBar.scss'

interface OverflowMenuProps {
  items: React.ReactNode[]
  label?: string
  onOpen?: () => void
  onClose?: () => void
  className?: string
}

type DropDownPosition = { top: number; left: number; width: number }

const SHIFT_UP_PX = 4

@observer
export default class OverflowMenu extends React.Component<OverflowMenuProps> {
  @observable public open: boolean = false
  @observable public dropdownPos: DropDownPosition = {
    top: 0,
    left: 0,
    width: 0,
  }

  private moreBtnRef = React.createRef<HTMLDivElement>()
  private dropdownRef = React.createRef<HTMLDivElement>()

  public constructor(props: OverflowMenuProps) {
    super(props)
    makeObservable(this)
  }

  @action.bound
  public toggleMenu() {
    const { onOpen, onClose } = this.props

    if (this.open) {
      this.open = false
      onClose?.()
      return
    }

    this.open = true
    const btn = this.moreBtnRef.current
    if (btn) {
      // Position button
      const rect = btn.getBoundingClientRect()
      this.dropdownPos = {
        top: rect.bottom + window.scrollY - SHIFT_UP_PX,
        left: rect.left + window.scrollX,
        width: rect.width,
      }
    }

    onOpen?.()
  }

  @action.bound
  public handleClickOutside(event: MouseEvent) {
    if (!this.open) return
    const btn = this.moreBtnRef.current
    const dropdown = this.dropdownRef.current
    const target = event.target as Node
    if (
      (btn && btn.contains(target)) ||
      (dropdown && dropdown.contains(target))
    ) {
      // Click inside button or dropdown, do nothing
      return
    }
    // Click outside
    this.open = false
    if (this.props.onClose) this.props.onClose()
  }

  @action.bound
  public handleWindowResize() {
    if (this.open) {
      this.open = false
    }
  }

  public componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside)
    window.addEventListener('resize', this.handleWindowResize)
    window.addEventListener('orientationchange', this.handleWindowResize)
  }

  public componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside)
    window.removeEventListener('resize', this.handleWindowResize)
    window.removeEventListener('orientationchange', this.handleWindowResize)
  }

  public renderDropdown(): JSX.Element {
    const { items } = this.props
    return createPortal(
      <div
        className="navbar-overflow-dropdown flex-nowrap absolute bg-white brada4"
        ref={this.dropdownRef}
        style={{
          top: this.dropdownPos.top,
          left: this.dropdownPos.left,
          minWidth: this.dropdownPos.width,
        }}
      >
        {items.map((item, i) => {
          if (!React.isValidElement(item)) {
            return item
          }

          const origClass = (item.props as any).className || ''
          return React.cloneElement(item, {
            key: i,
            ...item.props,
            className:
              (origClass ? origClass + ' ' : '') +
              'navbar-item text large flex-unset px12 nowrap navbar-overflow-item px16 pointer',
          })
        })}
      </div>,
      document.body,
      'overflow-menu-portal',
    )
  }

  public render() {
    const { className, label } = this.props

    return (
      <>
        <div className="navbar-overflow-menu inline-block relative">
          <div
            className={classList({
              'navbar-item text large center flex-unset nowrap more-button row no-select pointer': true,
              [className]: !!className,
            })}
            onClick={this.toggleMenu}
            ref={this.moreBtnRef}
            data-testid="navbar-overflow-menu"
          >
            <div className="navbar-item-wrapper">
              {label || Localization.translator.more}

              <ChevronDownIcon
                size={12}
                className={classList({
                  'ml7 vertical-align-middle': true,
                  rotate180: this.open,
                })}
              />
            </div>
          </div>
        </div>
        {this.open && this.renderDropdown()}
      </>
    )
  }
}
